import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Trophy, Clock, Target, BookOpen, ArrowLeft } from 'lucide-react';

const SUBJECT_COLORS = {
  Mathematics: '#6366f1',
  Physics: '#22c55e',
  Chemistry: '#f59e0b'
};

export default function ResultPage() {
  const { testId } = useParams();
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchResult();
  }, [testId]);

  const fetchResult = async () => {
    try {
      const { data } = await api.get(`/results/my/${testId}`);
      setResult(data.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load result');
      navigate('/student/dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center">
      <div className="loader"></div>
    </div>
  );

  if (!result) return null;

  const percentage = Math.round((result.totalScore / result.maxScore) * 100);
  const timeTakenMin = Math.floor(result.timeTaken / 60);
  const timeTakenSec = result.timeTaken % 60;



  const getGrade = (pct) => {
    if (pct >= 90) return { label: 'Outstanding', color: 'text-green-400' };
    if (pct >= 75) return { label: 'Excellent', color: 'text-blue-400' };
    if (pct >= 60) return { label: 'Good', color: 'text-primary-400' };
    if (pct >= 45) return { label: 'Average', color: 'text-amber-400' };
    return { label: 'Needs Improvement', color: 'text-red-400' };
  };

  const grade = getGrade(percentage);

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/student/dashboard')} className="text-gray-500 hover:text-black">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-black">Exam Results</h1>
            <p className="text-gray-500 text-sm">{result.test?.title}</p>
          </div>
        </div>

        {/* Score Card */}
        <div className="bg-white border border-gray-200 shadow-sm rounded-3xl p-6 sm:p-8 text-center">
          <div className="mb-4">
            {result.rank <= 3 && (
              <div className="text-5xl mb-2">
                {result.rank === 1 ? '🥇' : result.rank === 2 ? '🥈' : '🥉'}
              </div>
            )}
            <div className="inline-flex items-center justify-center w-32 h-32 rounded-full bg-yellow-100 shadow-md shadow-yellow-200 mb-4">
              <div className="text-center">
                <p className="text-4xl font-black text-black">{result.totalScore}</p>
                <p className="text-gray-600 text-xs">/{result.maxScore}</p>
              </div>
            </div>
          </div>

          <h2 className={`text-2xl font-bold mb-1 ${grade.color}`}>{grade.label}!</h2>
          <p className="text-gray-500 text-sm mb-6">{percentage}% score achieved</p>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
            <div className="bg-green-500/20 border border-green-500/30 rounded-xl p-3">
              <p className="text-2xl font-bold text-green-300">{result.correctAnswers}</p>
              <p className="text-green-400 text-xs mt-1">Correct</p>
            </div>
            <div className="bg-red-500/20 border border-red-500/30 rounded-xl p-3">
              <p className="text-2xl font-bold text-red-300">{result.incorrectAnswers}</p>
              <p className="text-red-400 text-xs mt-1">Incorrect</p>
            </div>
            <div className="bg-gray-500/20 border border-gray-500/30 rounded-xl p-3">
              <p className="text-2xl font-bold text-gray-300">{result.unattempted}</p>
              <p className="text-gray-400 text-xs mt-1">Unattempted</p>
            </div>
            <div className="bg-primary-500/20 border border-primary-500/30 rounded-xl p-3">
              <p className="text-2xl font-bold text-primary-300">{result.accuracy}%</p>
              <p className="text-primary-400 text-xs mt-1">Accuracy</p>
            </div>
          </div>

          <div className="flex flex-wrap justify-center gap-4 mt-4 text-sm text-gray-500">
            <span className="flex items-center gap-1.5">
              <Trophy size={14} className="text-yellow-500" />
              Rank: <strong className="text-black">{result.rank ? `#${result.rank}` : 'N/A'}</strong>
            </span>
            <span className="flex items-center gap-1.5">
              <Target size={14} className="text-blue-500" />
              Percentile: <strong className="text-black">{result.percentile}%ile</strong>
            </span>
            <span className="flex items-center gap-1.5">
              <Clock size={14} className="text-green-500" />
              Time: <strong className="text-black">{timeTakenMin}m {timeTakenSec}s</strong>
            </span>
          </div>
        </div>

        {/* Subject-wise Performance */}
        <div className="bg-white border border-gray-200 shadow-sm rounded-2xl p-6">
          <h3 className="font-bold text-black mb-4 flex items-center gap-2">
            <BookOpen size={18} className="text-yellow-500" /> Subject-wise Performance
          </h3>
          <div className="space-y-4">
            {result.subjectResults?.map(s => (
              <div key={s.subject}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-sm font-medium" style={{ color: SUBJECT_COLORS[s.subject] }}>{s.subject}</span>
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <span>Score: <strong className="text-black">{s.score?.toFixed(2)}</strong></span>
                    <span>{s.correct}/{s.totalQuestions} correct</span>
                    <span className={s.accuracy >= 70 ? 'text-green-600' : s.accuracy >= 50 ? 'text-yellow-600' : 'text-red-600'}>
                      {s.accuracy}%
                    </span>
                  </div>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-1000"
                    style={{ width: `${s.accuracy}%`, backgroundColor: SUBJECT_COLORS[s.subject] }}
                  ></div>
                </div>
                <div className="flex gap-4 mt-1 text-xs text-gray-500">
                  <span>✅ {s.correct} correct</span>
                  <span>❌ {s.incorrect} incorrect</span>
                  <span>○ {s.totalQuestions - s.attempted} skipped</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Email notice */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
          <p className="text-sm text-blue-800">
            📧 Your <strong>Response Sheet</strong> has been sent to your registered email address. Check your inbox.
          </p>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button onClick={() => navigate('/student/dashboard')}
            className="flex-1 bg-yellow-400 hover:bg-yellow-500 text-black font-semibold py-3 px-4 rounded-xl transition-colors">
            ← Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
}
